/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState } from 'react';
import noop from 'lodash/noop';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { cssMap, jsx } from '@atlaskit/css';
import AddIcon from '@atlaskit/icon/core/add';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	useHierarchyActions,
	type ItemDetails,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import type { siblingIssueCreateButton_nativeIssueTable$key } from '@atlassian/jira-relay/src/__generated__/siblingIssueCreateButton_nativeIssueTable.graphql';
import { fg } from '@atlassian/jira-feature-gating';
import { RowDropIndicator } from '../../../../common/ui/row-drop-indicator/index.tsx';
import { RowPopupButton } from '../../../../common/ui/row-popup-button/index.tsx';
import { useIsSiblingIssueCreateActive } from '../../../../controllers/sibling-create-active-index/index.tsx';
import { ISSUE } from '../../../../controllers/sibling-create-active-index/types.tsx';
import { useSiblingIssueCreateRow } from '../../../../controllers/sibling-issue-create-handler/index.tsx';
import { useRowContext } from '../../../rows/row-context/index.tsx';
import messages from './messages.tsx';

const styles = cssMap({
	button: {
		height: '24px',
		width: '24px',
		position: 'relative',
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		boxShadow: token('elevation.shadow.overlay'),
	},
});

type Props = {
	/** Index of the issue within a connection. */
	edgeIndex: number;
	/** Relay ID of the issue connection record. */
	issuesConnectionId: string;
	issue?: siblingIssueCreateButton_nativeIssueTable$key | null;
};

// Negatively offset the popup button by 1px to account for the cell border
const offset: [number, number] = [-1, -1];

/**
 * Display a 'Create' popup button positioned relative to the nearest containing block that will appear when hovering
 * between issue rows.
 */
export const SiblingIssueCreateButton = ({ issuesConnectionId, edgeIndex, issue }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	let groupId: string | undefined;
	let setIsSiblingCreateVisible: (itemDetails: ItemDetails, isVisible: boolean) => void = noop;
	if (fg('jira_timeline_harmonisation_m1_targeting')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		setIsSiblingCreateVisible = useHierarchyActions().setIsSiblingCreateVisible;
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		groupId = useRowContext().groupId;
	}

	const issueData = useFragment<siblingIssueCreateButton_nativeIssueTable$key>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment siblingIssueCreateButton_nativeIssueTable on JiraIssue {
				issueId
			}
		`,
		issue,
	);

	const [isInlineIssueCreateActive, { lockIndex, clearIndexAfterDelay }] =
		useIsSiblingIssueCreateActive([issuesConnectionId, edgeIndex]);
	const [isMouseOverSiblingCreate, setIsMouseOverSiblingCreate] = useState(false);

	const { triggerProps } = useSiblingIssueCreateRow();

	const handleMouseEnter = useCallback(() => {
		setIsMouseOverSiblingCreate(true);
		lockIndex(ISSUE);
	}, [lockIndex]);

	const handleMouseLeave = useCallback(() => {
		setIsMouseOverSiblingCreate(false);
		clearIndexAfterDelay(ISSUE);
	}, [clearIndexAfterDelay]);

	const handleOnClick = useCallback(() => {
		triggerProps?.onClick();

		if (issueData?.issueId && fg('jira_timeline_harmonisation_m1_targeting')) {
			setIsSiblingCreateVisible?.(
				{ itemsConnectionId: issuesConnectionId, itemId: issueData.issueId, groupId },
				true,
			);
		}

		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			}),
			'siblingIssueCreateButton',
		);
	}, [
		createAnalyticsEvent,
		groupId,
		issueData?.issueId,
		issuesConnectionId,
		setIsSiblingCreateVisible,
		triggerProps,
	]);

	return (
		<>
			<RowPopupButton
				alignToYAxis
				icon={<AddIcon label="" />}
				isVisible={isInlineIssueCreateActive}
				label={formatMessage(messages.createLabel)}
				offset={offset}
				onClick={handleOnClick}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				ref={triggerProps?.ref}
				tooltipContent={formatMessage(messages.createLabel)}
				tooltipPosition="left"
				xcss={styles.button}
			/>
			{isMouseOverSiblingCreate && <RowDropIndicator edge="top" />}
		</>
	);
};
